import {
  Box,
  Button,
  HStack,
  IconButton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useFormContext } from "react-hook-form";
import { AdSetFormData } from "../forms/adset";
import { useCallback, useState } from "react";
import WhiteBgInput from "../../../Guestlist/WhiteBgInput";
import { StepProps } from "../types";
import { FaMinus, FaPlus } from "react-icons/fa";
import IconWrapper from "./IconWrapper";

const EditInterestsWidget = ({
  isLoading = false,
  search,
}: {
  isLoading?: boolean;
  search?: StepProps["search"];
}) => {
  const { setValue, register, watch, getValues } =
    useFormContext<AdSetFormData>();
  const [interestsSearchResults, setInterestsSearchResults] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);

  const targetingType = "interests";
  const searchInterests = useCallback(
    (query: string) => {
      if (!search?.interests || !query.trim()) {
        setInterestsSearchResults([]);
        return Promise.resolve();
      }
      return search.interests(query).then((data) => {
        setInterestsSearchResults(data.data);
      });
    },
    [search]
  );

  const handleSearch = useCallback(() => {
    const query = watch("targeting_query");
    if (query) {
      searchInterests(query);
    }
  }, [watch, searchInterests]);

  const interestsOptions = (watch("interests") || []).map((i) => ({
    type: "interests",
    value: i.key || i.value,
    key: i.key,
    name: i.value || i.key,
  }));

  const isSelected = useCallback(
    (interest: { id: string; name: string }) => {
      return interestsOptions.some((i) => i.key === interest.id);
    },
    [interestsOptions]
  );

  const removeInterest = useCallback(
    (interest: { type: string; value?: string; key?: string }) => {
      const options = getValues("interests");
      const formattedInterest = {
        key: interest.key || interest.value,
      };

      setValue(
        "interests",
        options.filter((o) => o.key !== formattedInterest.key),
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    },
    [setValue, getValues]
  );

  const addInterest = useCallback(
    (interest: { id: string; name: string }) => {
      const options = watch("interests") || [];
      setValue(
        "interests",
        [
          ...options,
          {
            key: interest.id,
            value: interest.name,
          },
        ],
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    },
    [setValue, watch]
  );

  return (
    <VStack w="full">
      <HStack w="full" justifyContent="space-between">
        <WhiteBgInput
          placeholder="Search interests"
          {...register(`targeting_query`)}
          isDisabled={isLoading}
          flexGrow={1}
          w="full"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSearch();
            }
          }}
        />
        <Button flexShrink={0} isLoading={isLoading} onClick={handleSearch}>
          Search
        </Button>
      </HStack>
      <VStack w="full" px={4} maxH="500px" overflowY="scroll">
        {interestsSearchResults.map((interest) => (
          <HStack w="full" justifyContent="space-between" key={interest.id}>
            <Text>{interest.name}</Text>
            <IconButton
              variant="ghost"
              isLoading={isLoading}
              isDisabled={isLoading}
              onClick={() =>
                isSelected(interest)
                  ? removeInterest({
                      type: targetingType,
                      key: interest.id,
                      value: interest.name,
                    })
                  : addInterest(interest)
              }
              aria-label="Toggle interest"
              icon={isSelected(interest) ? <FaMinus /> : <FaPlus />}
            />
          </HStack>
        ))}
      </VStack>
      <Box w="full">
        <HStack
          w="full"
          gap={1}
          justifyContent={"flex-start"}
          flexWrap={"wrap"}
        >
          {interestsOptions.map((interest, index) => (
            <IconWrapper
              key={interest.key || index}
              item={interest}
              isLoading={isLoading}
              onRemove={removeInterest}
            />
          ))}
        </HStack>
      </Box>
    </VStack>
  );
};

export default EditInterestsWidget;
